// @ts-nocheck
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useAuth } from '@/lib/AuthContext';
import { useLanguage } from '@/components/ui/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Play, 
  Globe, 
  Zap, 
  BookOpen,
  ChevronRight,
  Check,
  MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Landing() {
  const { t, language, changeLanguage } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/Home');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = () => {
    navigate('/Login');
  };

  const features = [
    {
      icon: Zap,
      title: language === 'ja' ? '高品質な動画コンテンツ' : 'High-Quality Videos',
      description: language === 'ja' 
        ? 'プロが制作した分かりやすい解説動画で効率的に学習'
        : 'Learn efficiently with professional, easy-to-understand videos'
    },
    {
      icon: Globe,
      title: language === 'ja' ? '多言語字幕対応' : 'Multi-language Subtitles',
      description: language === 'ja'
        ? '日本語・英語など複数言語の字幕で世界中から学習可能'
        : 'Available subtitles in Japanese, English, and more'
    },
    {
      icon: BookOpen,
      title: language === 'ja' ? '体系的なカリキュラム' : 'Structured Curriculum',
      description: language === 'ja'
        ? 'シリーズ形式で基礎から応用まで順序立てて学習'
        : 'Learn systematically from basics to advanced topics'
    },
    {
      icon: MessageCircle,
      title: language === 'ja' ? 'Discordコミュニティ' : 'Discord Community',
      description: language === 'ja'
        ? '学習仲間と繋がり、質問やディスカッションが可能'
        : 'Connect with fellow learners, ask questions, and discuss'
    }
  ];

  const plans = [
    {
      name: t('freePlan'),
      price: '¥0',
      period: '',
      features: [
        language === 'ja' ? '無料動画の視聴' : 'Watch free videos',
        language === 'ja' ? '進捗管理' : 'Progress tracking',
        language === 'ja' ? 'Discord参加' : 'Discord access'
      ],
      highlighted: false
    },
    {
      name: t('monthlyPlan'),
      price: '¥1,980',
      period: t('perMonth'),
      features: [
        language === 'ja' ? '全動画視聴し放題' : 'Unlimited video access',
        language === 'ja' ? '練習資料ダウンロード' : 'Download materials',
        language === 'ja' ? '優先サポート' : 'Priority support',
        language === 'ja' ? '限定コンテンツ' : 'Exclusive content'
      ],
      highlighted: true
    },
    {
      name: t('yearlyPlan'),
      price: '¥19,800',
      period: t('perYear'),
      features: [
        language === 'ja' ? '月額プランの全機能' : 'All monthly features',
        language === 'ja' ? '2ヶ月分お得' : 'Save 2 months',
        language === 'ja' ? '特別バッジ' : 'Special badge',
        language === 'ja' ? '先行アクセス' : 'Early access'
      ],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="fixed inset-0 pointer-events-none opacity-30">
        <div className="absolute top-10 left-10 w-32 h-32 border-2 border-[#8b6f47] rounded-full opacity-20" />
        <div className="absolute top-40 right-20 w-20 h-20 border-2 border-[#8b6f47] rounded-full opacity-20" />
        <div className="absolute bottom-20 left-1/4 w-24 h-24 border-2 border-[#8b6f47] rounded-full opacity-20" />
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute text-[#c9a86a] opacity-40"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              fontSize: `${8 + Math.random() * 8}px`
            }}
          >
            ✦
          </div>
        ))}
      </div>



      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b-2 border-[#8b6f47] bg-white/90 backdrop-blur-lg shadow-lg">
        <div className="absolute inset-0"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-between h-16">
            <Link to={isAuthenticated ? createPageUrl('Home') : createPageUrl('Landing')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#8b6f47] flex items-center justify-center">
                <svg className="w-4 h-4 text-[#f5ede3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
              <span className="font-bold text-xl text-[#3d2817]" style={{ fontFamily: 'serif' }}>イラストアカデミア</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <Link
                to={createPageUrl('Home')}
                className="text-[#5d4529] hover:text-[#3d2817] hover:bg-[#f5ede3] px-4 py-2 rounded-lg transition-all border-2 border-transparent hover:border-[#d4c4b0] text-sm font-medium"
              >
                {language === 'ja' ? 'ホーム' : 'Home'}
              </Link>

              <Link
                to={createPageUrl('Catalog')}
                className="text-[#5d4529] hover:text-[#3d2817] hover:bg-[#f5ede3] px-4 py-2 rounded-lg transition-all border-2 border-transparent hover:border-[#d4c4b0] text-sm font-medium"
              >
                {language === 'ja' ? '講座' : 'Courses'}
              </Link>

              <Link
                to={createPageUrl('MyPage')}
                className="text-[#5d4529] hover:text-[#3d2817] hover:bg-[#f5ede3] px-4 py-2 rounded-lg transition-all border-2 border-transparent hover:border-[#d4c4b0] text-sm font-medium"
              >
                {language === 'ja' ? 'マイページ' : 'My Page'}
              </Link>

              <Link
                to={createPageUrl('Help')}
                className="text-[#5d4529] hover:text-[#3d2817] hover:bg-[#f5ede3] px-4 py-2 rounded-lg transition-all border-2 border-transparent hover:border-[#d4c4b0] text-sm font-medium"
              >
                {language === 'ja' ? 'ヘルプ' : 'Help'}
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-1 bg-[#f5ede3] rounded-lg p-1">
                <button
                  onClick={() => changeLanguage('ja')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    language === 'ja' ? 'bg-[#3d2817] text-[#f5ede3]' : 'text-[#6b5540] hover:text-[#3d2817]'
                  }`}
                >
                  JP
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    language === 'en' ? 'bg-[#3d2817] text-[#f5ede3]' : 'text-[#6b5540] hover:text-[#3d2817]'
                  }`}
                >
                  EN
                </button>
              </div>

              {!isAuthenticated ? (
                <Button
                  size="sm"
                  onClick={handleLogin}
                  className="bg-[#3d2817] hover:bg-[#2d1810] text-[#f5ede3] border-2 border-[#8b6f47] shadow-md"
                >
                  {language === 'ja' ? 'ログイン' : 'Login'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => {
                    window.location.href = '/Home';
                  }}
                  className="bg-[#8b6f47] hover:bg-[#6b5540] text-[#f5ede3] border-2 border-[#8b6f47] shadow-md"
                >
                  {language === 'ja' ? 'マイページへ' : 'My Page'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
        style={{
          backgroundImage: 'url(https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/694df6267ee2f5ae746abedd/91e70cf4a_.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-white/70"></div>
        
        {/* Decorative ornamental frame */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-64 h-64">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#8b6f47] to-transparent" />
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-[#8b6f47] to-transparent" />
          </div>
          <div className="absolute top-0 right-0 w-64 h-64">
            <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-[#8b6f47] to-transparent" />
            <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-transparent via-[#8b6f47] to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 w-64 h-64">
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#8b6f47] to-transparent" />
            <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-[#8b6f47] to-transparent" />
          </div>
          <div className="absolute bottom-0 right-0 w-64 h-64">
            <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-[#8b6f47] to-transparent" />
            <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-b from-transparent via-[#8b6f47] to-transparent" />
          </div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Magical emblem */}
            <div className="mb-8 flex justify-center">
              <div className="relative w-32 h-32">
                <div className="absolute inset-0 bg-[#c9a86a] rounded-full blur-2xl opacity-30 animate-pulse" />
                <div className="relative w-full h-full bg-[#3d2817] rounded-full flex items-center justify-center">
                  <svg className="w-16 h-16 text-[#c9a86a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="mb-6 inline-block bg-white/60 text-[#3d2817] px-6 py-2.5 rounded-full text-base font-semibold">
              <span className="text-[#c9a86a]">✦</span> {language === 'ja' ? '新規生徒募集中' : 'Now Accepting Students'} <span className="text-[#c9a86a]">✦</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-[#2d1810] leading-tight drop-shadow-lg" style={{ fontFamily: 'serif' }}>
              {language === 'ja' ? '魔法のような学びの世界へ' : 'Enter a Magical Learning World'}
            </h1>
            
            <p className="text-xl md:text-2xl text-[#5d4529] mb-10 max-w-2xl mx-auto">
              {language === 'ja' 
                ? '仲間と共に成長する、ファンタジー学習アカデミア' 
                : 'A Fantasy Learning Academia - Grow Together with Companions'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Link to={createPageUrl('Home')}>
                  <Button size="lg" className="bg-[#8b6f47] hover:bg-[#6b5540] text-white text-lg px-10 py-7 rounded-xl font-bold">
                    <Play className="w-5 h-5 mr-2 fill-current" />
                    {language === 'ja' ? '冒険を始める' : 'Start Adventure'}
                  </Button>
                </Link>
              ) : (
                <Button 
                  size="lg" 
                  onClick={handleLogin}
                  className="bg-[#3d2817] hover:bg-[#2d1810] text-[#f5ede3] text-lg px-10 py-7 rounded-xl font-bold"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  Discordで入学申込
                </Button>
              )}
            </div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="w-1.5 h-1.5 bg-white rounded-full mt-2"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative" id="features">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4 px-6 py-3 bg-white/60 backdrop-blur-sm border-2 border-[#8b6f47] rounded-full shadow-lg">
              <h2 className="text-3xl md:text-4xl font-bold text-[#3d2817]" style={{ fontFamily: 'serif' }}>
                <span className="text-[#c9a86a]">✦</span> {language === 'ja' ? 'アカデミアの特徴' : 'Academia Features'} <span className="text-[#c9a86a]">✦</span>
              </h2>
            </div>
            <p className="text-[#5d4529] max-w-2xl mx-auto text-lg font-medium">
              {language === 'ja' 
                ? '魔法のように充実した学習体験'
                : 'Magically Enhanced Learning Experience'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/80 backdrop-blur-sm border-2 border-[#d4c4b0] hover:border-[#8b6f47] transition-all group shadow-xl hover:shadow-2xl"
              >
                <div className="w-14 h-14 rounded-xl bg-[#8b6f47] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-[#f5ede3]" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-[#3d2817]">{feature.title}</h3>
                <p className="text-sm text-[#5d4529]">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 relative bg-gradient-to-b from-[#efe5d8] to-[#e8ddc8]" id="pricing">
        <div className="max-w-5xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4 px-6 py-3 bg-white/80 rounded-full">
              <h2 className="text-3xl md:text-4xl font-bold text-[#3d2817]" style={{ fontFamily: 'serif' }}>
                <span className="text-[#c9a86a]">✦</span> {language === 'ja' ? '受講プラン' : 'Tuition Plans'} <span className="text-[#c9a86a]">✦</span>
              </h2>
            </div>
            <p className="text-[#5d4529] max-w-2xl mx-auto text-lg font-medium">
              {language === 'ja'
                ? 'あなたに合った学びのスタイルを選択'
                : 'Choose Your Learning Path'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-8 rounded-2xl ${
                  plan.highlighted 
                    ? 'bg-[#8b6f47]' 
                    : 'bg-white'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#c9a86a] text-[#2d1810] px-4 py-1 rounded-full text-sm font-bold">
                    ⭐ {language === 'ja' ? '人気No.1' : 'Most Popular'} ⭐
                  </div>
                )}
                
                <h3 className={`font-bold text-xl mb-4 ${plan.highlighted ? 'text-[#f5ede3]' : 'text-[#3d2817]'}`}>{plan.name}</h3>
                <div className="mb-6">
                  <span className={`text-5xl font-bold ${plan.highlighted ? 'text-white' : 'text-[#2d1810]'}`}>{plan.price}</span>
                  <span className={`text-lg ml-1 ${plan.highlighted ? 'text-[#f5ede3]' : 'text-[#6b5540]'}`}>{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`flex items-center gap-2 text-sm ${plan.highlighted ? 'text-[#f5ede3]' : 'text-[#5d4529]'}`}>
                      <Check className={`w-5 h-5 flex-shrink-0 ${plan.highlighted ? 'text-[#c9a86a]' : 'text-[#8b6f47]'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  className={`w-full font-bold ${
                    plan.highlighted 
                      ? 'bg-[#c9a86a] hover:bg-[#b8956a] text-[#2d1810]' 
                      : 'bg-[#8b6f47] hover:bg-[#6b5540] text-white'
                  }`}
                  onClick={handleLogin}
                >
                  {t('selectPlan')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#3d2817] relative">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8b6f47] flex items-center justify-center">
                <svg className="w-6 h-6 text-[#3d2817]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
              <span className="font-bold text-2xl text-[#f5ede3]" style={{ fontFamily: 'serif' }}>イラストアカデミア</span>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-[#d4c4b0]">
              <Link to={createPageUrl('Terms')} className="hover:text-[#c9a86a] transition-colors">
                {t('terms')}
              </Link>
              <Link to={createPageUrl('Commerce')} className="hover:text-[#c9a86a] transition-colors">
                {t('commerce')}
              </Link>
              <Link to={createPageUrl('Privacy')} className="hover:text-[#c9a86a] transition-colors">
                {t('privacy')}
              </Link>
              <Link to={createPageUrl('Help')} className="hover:text-[#c9a86a] transition-colors">
                {t('contact')}
              </Link>
            </div>

            <p className="text-sm text-[#8b6f47]">
              © 2024 LearnHub. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}